import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { uploadService } from '../services/uploadService';
import { paymentExtractionService, orderService, type Order, type OrderStatus } from '../services';
import { companyService } from '../services/companyService';
import LazyImage from '../components/LazyImage';
import { generateOrderReceiptHTML } from '../utils/orderReceiptTemplate';
import { generateOrderReceiptPDF } from '../utils/orderReceiptPDF';

const EnquiryDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  // const navigate = useNavigate(); // Unused
  const { user } = useAuth();
  const { showToast } = useToast();
  
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [statusHistory, setStatusHistory] = useState<any[]>([]);
  const [companyInfo, setCompanyInfo] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (user && id) {
      loadOrderDetails();
      loadCompanyInfo();
    }
  }, [user, id]);

  const loadOrderDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await orderService.getOrderById(id!);
      
      if (response.success && response.data) {
        setOrder(response.data);
        // Load status history after order is loaded
        loadStatusHistory();
      } else {
        setError(response.error || 'Failed to load order details');
      }
    } catch (err) {
      setError('Failed to load order details');
      console.error('Error loading order:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStatusHistory = async () => {
    try {
      const response = await orderService.getStatusHistory(id!);
      if (response.success && response.data) {
        setStatusHistory(response.data);
      }
    } catch (err) {
      console.error('Error loading status history:', err);
    }
  };

  const loadCompanyInfo = async () => {
    try {
      const response = await companyService.getCompanyInfo();
      if (response.success && response.data) {
        setCompanyInfo(response.data);
      }
    } catch (err) {
      console.error('Error loading company info:', err);
      // Don't show error to user as it's not critical
    }
  };

  const getStatusColor = (status: OrderStatus) => {
    const colors: Record<OrderStatus, string> = {
      pending: 'bg-yellow-100 text-yellow-700',
      confirmed: 'bg-blue-100 text-blue-700',
      processing: 'bg-orange-100 text-orange-700',
      ready_for_pickup: 'bg-purple-100 text-purple-700',
      ready_for_delivery: 'bg-indigo-100 text-indigo-700',
      out_for_delivery: 'bg-cyan-100 text-cyan-700',
      shipped: 'bg-sky-100 text-sky-700',
      delivered: 'bg-green-100 text-green-700',
      completed: 'bg-emerald-100 text-emerald-700',
      cancelled: 'bg-red-100 text-red-700',
      refunded: 'bg-gray-100 text-gray-700',
      on_hold: 'bg-amber-100 text-amber-700'
    };
    return colors[status] || 'bg-neutral-100 text-neutral-700';
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleUploadPaymentProof = async (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('🚀 Upload function started');
    const file = e.target.files?.[0];
    
    if (!file || !order) {
      console.log('❌ No file or order found');
      e.target.value = '';
      return;
    }
    
    console.log('📁 File selected:', file.name, file.type, file.size);

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showToast('Please upload an image file (PNG, JPG, etc.)', 'error');
      e.target.value = '';
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showToast('File size must be less than 5MB', 'error');
      e.target.value = '';
      return;
    }

    setUploadingProof(true);

    try {
      // First, validate the payment receipt using AI
      const fileReader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        fileReader.onload = () => {
          const result = fileReader.result as string;
          resolve(result);
        };
        fileReader.onerror = () => reject(new Error('Failed to read file'));
        fileReader.readAsDataURL(file);
      });

      const base64Data = await base64Promise;
      
      // Call AI validation service to check if it's a valid payment receipt
      console.log('🔍 Starting AI validation for file:', file.name, file.type);
      const validationResult = await paymentExtractionService.extractPaymentInfo({
        imageData: base64Data,
        mimeType: file.type
      });
      
      console.log('🤖 AI validation result:', validationResult);
      
      if (!validationResult.success) {
        console.error('❌ AI validation failed:', validationResult.error);
        throw new Error('Failed to validate payment receipt');
      }

      const extractedData = validationResult.data;
      console.log('📊 Extracted data:', extractedData);
      
      // Handle null or undefined extractedData
      if (!extractedData) {
        showToast('Failed to analyze the image. Please upload a clear payment receipt.', 'error');
        e.target.value = '';
        setUploadingProof(false);
        return;
      }
      
      // Check if it looks like a payment receipt with stricter validation
      const confidence = extractedData.confidence || 0;
      const amount = extractedData.amount || 0;
      const rawText = extractedData.rawText || '';
      
      const isValidPaymentReceipt = 
        confidence >= 0.5 && // Higher confidence threshold
        amount > 0 && 
        amount >= 1 && // Minimum amount of ₹1
        amount <= 10000000 && // Maximum reasonable amount (1 crore)
        (extractedData.transactionId || extractedData.paymentMethod || extractedData.status) && // Must have at least one payment identifier
        rawText !== "Not a payment receipt" && // Explicit AI rejection
        !rawText.toLowerCase().includes('not a payment'); // Additional safety check
      
      if (!isValidPaymentReceipt) {
        const reasons = [];
        if (rawText === "Not a payment receipt" || rawText.toLowerCase().includes('not a payment')) {
          reasons.push('AI determined this is not a payment receipt');
        }
        if (confidence < 0.5) reasons.push(`Low confidence (${(confidence * 100).toFixed(1)}%)`);
        if (amount <= 0) reasons.push('No valid amount detected');
        if (amount < 1) reasons.push('Amount too small');
        if (amount > 10000000) reasons.push('Amount suspiciously high');
        if (!extractedData.transactionId && !extractedData.paymentMethod && !extractedData.status) {
          reasons.push('No payment identifiers found');
        }
        
        console.log('🚫 Validation failed - reasons:', reasons);
        console.log('📋 Validation details:', { confidence, amount, rawText, hasTransactionId: !!extractedData.transactionId, hasPaymentMethod: !!extractedData.paymentMethod, hasStatus: !!extractedData.status });
        
        showToast(
          `This does not appear to be a valid payment receipt. ${reasons.length > 0 ? `Issues: ${reasons.join(', ')}` : 'Please upload a clear payment screenshot with transaction details.'}`, 
          'error'
        );
        e.target.value = '';
        setUploadingProof(false);
        return;
      }
      
      console.log('✅ Validation passed - proceeding with upload');

      // Check if transaction ID is already used (if extracted)
      if (extractedData.transactionId) {
        const duplicateResult = await orderService.checkTransactionId(extractedData.transactionId);
        
        if (duplicateResult.success && duplicateResult.data.exists) {
          showToast(`This transaction ID (${extractedData.transactionId}) has already been used for another order.`, 'error');
          e.target.value = '';
          setUploadingProof(false);
          return;
        }
      }

      // Upload the file
      const uploadResponse = await uploadService.uploadPaymentProof(file);
      
      if (!uploadResponse.success || !uploadResponse.data?.url) {
        throw new Error('Failed to upload payment proof');
      }

      // Update order with payment proof including extracted data
      const updateResponse = await orderService.uploadPaymentProof(order.id, {
        url: uploadResponse.data.url,
        fileName: file.name,
        fileType: file.type,
        extractedData: extractedData
      });

      if (updateResponse.success) {
        showToast(`Payment proof uploaded successfully! ${extractedData.amount > 0 ? `Amount detected: ₹${extractedData.amount}` : ''}`, 'success');
        // Reload order details and status history to show updated status
        loadOrderDetails();
        loadStatusHistory();
      } else {
        throw new Error(updateResponse.error || 'Failed to attach payment proof');
      }
    } catch (error) {
      console.error('Error uploading payment proof:', error);
      showToast(
        error instanceof Error ? error.message : 'Failed to upload payment proof',
        'error'
      );
    } finally {
      setUploadingProof(false);
      e.target.value = ''; // Reset file input
    }
  };

  const downloadOrderDetails = () => {
    if (!order) return;

    try {
      showToast('Generating PDF...', 'info');
      
      // Generate PDF directly using jsPDF
      const pdf = generateOrderReceiptPDF(order, user || undefined, companyInfo);
      
      // Save the PDF
      pdf.save(`enquiry-${order.orderNumber}-receipt.pdf`);
      
      showToast('Enquiry receipt downloaded successfully!', 'success');
    } catch (error) {
      console.error('Error downloading receipt:', error);
      showToast('Failed to download receipt', 'error');
    }
  };

  const printOrderDetails = () => {
    if (!order) return;

    try {
      // Generate receipt HTML with user info and company info
      const receiptHTML = generateOrderReceiptHTML(order, user || undefined, companyInfo);
      
      // Create a blob and open in new window for printing
      const blob = new Blob([receiptHTML], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');
      
      if (printWindow) {
        // Wait for content to load before triggering print
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            // Clean up after printing
            setTimeout(() => {
              window.URL.revokeObjectURL(url);
            }, 1000);
          }, 250);
        };
      } else {
        // Fallback if popup blocked
        showToast('Please allow popups to print the receipt', 'info');
      }
    } catch (error) {
      console.error('Error printing receipt:', error);
      showToast('Failed to print receipt', 'error');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-accent-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-neutral-600">Loading enquiry details...</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-12 text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-neutral-900 mb-2">Unable to load enquiry</h3>
            <p className="text-neutral-600 mb-6">{error || 'Enquiry not found'}</p>
            <Link
              to="/enquiries"
              className="inline-flex items-center gap-2 px-6 py-3 bg-accent-600 text-white rounded-xl font-semibold hover:bg-accent-700 transition-all duration-300"
            >
              Back to Enquiries
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <Link to="/enquiries" className="text-accent-600 hover:text-accent-700 text-sm mb-2 inline-flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Enquiries
              </Link>
              <h1 className="text-2xl font-bold text-neutral-900">Enquiry Details</h1>
            </div>
            <div className="flex items-center gap-3">
              {/* Print Button */}
              <button
                onClick={() => printOrderDetails()}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50 transition-colors"
                title="Print enquiry receipt"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                <span className="hidden sm:inline">Print</span>
              </button>
              
              {/* Download Button */}
              <button
                onClick={() => downloadOrderDetails()}
                className="inline-flex items-center gap-2 px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-colors"
                title="Download enquiry receipt"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="hidden sm:inline">Download</span>
              </button>
              
              {/* Status Badge */}
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>
                {order.status.charAt(0).toUpperCase() + order.status.slice(1).replace(/_/g, ' ')}
              </span>
            </div>
          </div>
        </div>

        {/* Order Info Card */}
        <div className="bg-white rounded-xl shadow-sm border border-neutral-100 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-neutral-600 mb-1">Enquiry Number</p>
              <p className="font-semibold text-neutral-900">{order.orderNumber}</p>
            </div>
            <div>
              <p className="text-sm text-neutral-600 mb-1">Date</p>
              <p className="font-medium text-neutral-900">{formatDate(order.createdAt)}</p>
            </div>
            <div>
              <p className="text-sm text-neutral-600 mb-1">Payment Status</p>
              <p className={`font-medium ${order.paymentStatus === 'paid' ? 'text-green-600' : 'text-orange-600'}`}>
                {order.paymentStatus.charAt(0).toUpperCase() + order.paymentStatus.slice(1).replace(/_/g, ' ')}
              </p>
            </div>
            <div>
              <p className="text-sm text-neutral-600 mb-1">Total Amount</p>
              <p className="font-bold text-neutral-900 text-lg">₹{order.total.toLocaleString()}</p>
            </div>
          </div>
        </div>

        {/* Second Row: Order Timeline and Payment Proof side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Order Timeline */}
          {statusHistory && statusHistory.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-neutral-100 p-6">
              <h3 className="font-semibold text-neutral-900 mb-4">Order Timeline</h3>
              <div className="space-y-6 max-h-96 overflow-y-auto">
                {statusHistory.map((history, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        index === 0 ? 'bg-accent-600' : 'bg-accent-100'
                      }`}>
                        <svg className={`w-5 h-5 ${index === 0 ? 'text-white' : 'text-accent-600'}`} fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm1 14.93V17a1 1 0 01-2 0v-.07A7.003 7.003 0 015 10a1 1 0 012 0 5 5 0 105 5z"/>
                        </svg>
                      </div>
                      {index < statusHistory.length - 1 && (
                        <div className="w-0.5 h-full bg-neutral-200 ml-5 mt-2"></div>
                      )}
                    </div>
                    <div className="flex-1 pb-6">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-neutral-900">
                          {(history.newStatus || history.status || '').charAt(0).toUpperCase() + (history.newStatus || history.status || '').slice(1).replace(/_/g, ' ')}
                        </p>
                        {index === 0 && (
                          <span className="text-xs px-2 py-0.5 bg-accent-100 text-accent-700 rounded-full font-medium">
                            Current
                          </span>
                        )}
                      </div>
                      
                      <p className="text-sm text-neutral-600 mb-2">{formatDate(history.createdAt || history.timestamp || new Date())}</p>
                      
                      {history.updatedBy && (
                        <p className="text-xs text-neutral-500 mb-2">
                          Updated by: {history.updatedBy}
                          {history.updatedByEmail && ` (${history.updatedByEmail})`}
                        </p>
                      )}
                      
                      {history.notes && (
                        <div className="bg-neutral-50 rounded-lg p-3 mb-3">
                          <p className="text-sm text-neutral-700">{history.notes}</p>
                        </div>
                      )}
                      
                      {history.attachments && history.attachments.length > 0 && (
                        <div className="mt-3">
                          <p className="text-xs font-medium text-neutral-700 mb-2">Attachments ({history.attachments.length})</p>
                          <div className="grid grid-cols-1 gap-2">
                            {history.attachments.map((attachment: any, attachIndex: number) => (
                              <div 
                                key={attachIndex} 
                                className="flex items-center gap-2 p-2 bg-white border border-neutral-200 rounded-lg hover:border-accent-300 transition-colors cursor-pointer"
                                onClick={() => window.open(attachment.url, '_blank')}
                              >
                                {/* Thumbnail for images */}
                                {attachment.type && attachment.type.startsWith('image/') ? (
                                  <div className="flex-shrink-0 w-12 h-12 rounded overflow-hidden border border-neutral-200">
                                    <img
                                      src={attachment.url}
                                      alt={attachment.name}
                                      className="w-full h-full object-cover"
                                    />
                                  </div>
                                ) : (
                                  <div className="flex-shrink-0 w-12 h-12 rounded bg-neutral-100 flex items-center justify-center">
                                    <svg className="w-6 h-6 text-neutral-500" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                                    </svg>
                                  </div>
                                )}
                                
                                {/* File info */}
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-medium text-neutral-800 truncate" title={attachment.name}>
                                    {attachment.name}
                                  </p>
                                  {attachment.size && (
                                    <p className="text-xs text-neutral-500">
                                      {(attachment.size / 1024).toFixed(1)} KB
                                    </p>
                                  )}
                                </div>
                                
                                {/* Open icon */}
                                <svg className="w-4 h-4 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Payment Proof Section - Show when there are proofs or order is pending */}
          {(order.paymentProofs && order.paymentProofs.length > 0) || (order.status === 'pending' && order.paymentStatus !== 'paid') ? (
            <div className="bg-white rounded-xl shadow-sm border border-neutral-100 p-6">
              <h3 className="font-semibold text-neutral-900 mb-4">Payment Proof</h3>
              
              {/* Show existing payment proofs */}
              {order.paymentProofs && order.paymentProofs.length > 0 && (
                <div className={order.status === 'pending' ? "mb-4" : ""}>
                  <p className="text-sm font-medium text-neutral-700 mb-3">Uploaded Proofs ({order.paymentProofs.length})</p>
                  <div className="grid grid-cols-1 gap-3 max-h-64 overflow-y-auto">
                    {order.paymentProofs.map((proof, index) => (
                      <div key={index} className="border border-neutral-200 rounded-lg p-3 bg-neutral-50">
                        <div className="flex items-start gap-3">
                          {/* Thumbnail */}
                          {proof.url && (
                            <div className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border border-neutral-300">
                              <img
                                src={proof.url}
                                alt={`Payment proof ${index + 1}`}
                                className="w-full h-full object-cover cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => window.open(proof.url, '_blank')}
                              />
                            </div>
                          )}
                          
                          {/* Details */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                              </svg>
                              <span className="text-sm font-medium text-neutral-800">Proof {index + 1}</span>
                              {proof.status === 'verified' && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                  Verified
                                </span>
                              )}
                              {proof.status === 'pending' && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                  Pending
                                </span>
                              )}
                            </div>
                            {proof.fileName && (
                              <p className="text-xs text-neutral-600 truncate">{proof.fileName}</p>
                            )}
                            {proof.uploadedAt && (
                              <p className="text-xs text-neutral-500 mt-1">
                                Uploaded: {new Date(proof.uploadedAt).toLocaleString('en-IN', {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Upload button - only visible when order is pending */}
              {order.status === 'pending' && (
                <>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingProof}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-medium hover:from-green-600 hover:to-emerald-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {uploadingProof ? (
                      <>
                        <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                        <span>Uploading...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <span>{order.paymentProofs && order.paymentProofs.length > 0 ? 'Add More Payment Proof' : 'Upload Payment Proof'}</span>
                      </>
                    )}
                  </button>
                  <p className="text-sm text-neutral-600 mt-2">
                    {order.paymentProofs && order.paymentProofs.length > 0 
                      ? 'You can upload additional payment proofs if needed' 
                      : 'Upload your payment screenshot or receipt'}
                  </p>
                </>
              )}
            </div>
          ) : null}

        </div>

        {/* Products Table */}
        <div className="bg-white rounded-xl shadow-sm border border-neutral-100 p-6 mb-6">
          <h2 className="text-lg font-semibold text-neutral-900 mb-4">Products</h2>
          
          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">Product</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-neutral-700">SKU</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-neutral-700">Price</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-neutral-700">Quantity</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-neutral-700">Total</th>
                </tr>
              </thead>
              <tbody>
                {order.items.map((item, index) => (
                  <tr key={index} className="border-b hover:bg-gray-50">
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        <LazyImage
                          src={item.productImage || ''}
                          alt={item.productName}
                          className="w-12 h-12 object-cover rounded-lg"
                        />
                        <span className="font-medium text-neutral-900">{item.productName}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center text-sm text-neutral-600">{item.sku}</td>
                    <td className="py-4 px-4 text-center">₹{item.price.toLocaleString()}</td>
                    <td className="py-4 px-4 text-center font-medium">{item.quantity}</td>
                    <td className="py-4 px-4 text-right font-semibold">₹{item.total.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {order.items.map((item, index) => (
              <div key={index} className="border rounded-lg p-4">
                <div className="flex gap-3">
                  <LazyImage
                    src={item.productImage || ''}
                    alt={item.productName}
                    className="w-16 h-16 object-cover rounded-lg"
                  />
                  <div className="flex-1">
                    <h4 className="font-medium text-neutral-900">{item.productName}</h4>
                    <p className="text-sm text-neutral-600">SKU: {item.sku}</p>
                    <div className="mt-2 flex justify-between items-center">
                      <span className="text-sm">₹{item.price} × {item.quantity}</span>
                      <span className="font-semibold">₹{item.total.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="mt-6 pt-6 border-t">
            <div className="max-w-sm ml-auto space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-neutral-600">Subtotal</span>
                <span className="font-medium">₹{order.subtotal.toLocaleString()}</span>
              </div>
              {order.discount > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Discount {order.couponCode && `(${order.couponCode})`}</span>
                  <span>-₹{order.discount.toLocaleString()}</span>
                </div>
              )}
              {order.tax > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-600">Tax</span>
                  <span className="font-medium">₹{order.tax.toLocaleString()}</span>
                </div>
              )}
              {order.shipping > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-600">Shipping</span>
                  <span className="font-medium">₹{order.shipping.toLocaleString()}</span>
                </div>
              )}
              {order.packing > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-600">Packaging Charge</span>
                  <span className="font-medium">₹{order.packing.toLocaleString()}</span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t">
                <span className="font-semibold">Total</span>
                <span className="font-bold text-lg">₹{order.total.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Addresses */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-neutral-100 p-6">
            <h3 className="font-semibold text-neutral-900 mb-3">Shipping Address</h3>
            <div className="text-sm text-neutral-700 space-y-1">
              <p>{order.shippingAddress.street}</p>
              <p>{order.shippingAddress.city}, {order.shippingAddress.state}</p>
              <p>{order.shippingAddress.zipCode}</p>
              <p>{order.shippingAddress.country}</p>
            </div>
          </div>

          {order.billingAddress && (
            <div className="bg-white rounded-xl shadow-sm border border-neutral-100 p-6">
              <h3 className="font-semibold text-neutral-900 mb-3">Billing Address</h3>
              <div className="text-sm text-neutral-700 space-y-1">
                <p>{order.billingAddress.street}</p>
                <p>{order.billingAddress.city}, {order.billingAddress.state}</p>
                <p>{order.billingAddress.zipCode}</p>
                <p>{order.billingAddress.country}</p>
              </div>
            </div>
          )}
        </div>

        {/* Additional Info */}
        <div className="bg-white rounded-xl shadow-sm border border-neutral-100 p-6 mb-6">
          <h3 className="font-semibold text-neutral-900 mb-4">Additional Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
           
            <div>
              <p className="text-sm text-neutral-600 mb-1">Payment Status</p>
              <p className={`font-medium ${order.paymentStatus === 'paid' ? 'text-green-600' : order.paymentStatus === 'pending' ? 'text-yellow-600' : 'text-red-600'}`}>
                {order.paymentStatus.charAt(0).toUpperCase() + order.paymentStatus.slice(1).replace(/_/g, ' ')}
                {order.paymentProofs && order.paymentProofs.length > 0 && order.paymentStatus === 'pending' && (
                  <span className="text-xs text-yellow-600 ml-1">(Verification Pending)</span>
                )}
              </p>
            </div>
            {order.trackingNumber && (
              <div>
                <p className="text-sm text-neutral-600 mb-1">Tracking Number</p>
                <p className="font-mono font-medium text-neutral-900">{order.trackingNumber}</p>
              </div>
            )}
            {order.carrier && (
              <div>
                <p className="text-sm text-neutral-600 mb-1">Carrier</p>
                <p className="font-medium text-neutral-900">{order.carrier}</p>
              </div>
            )}
            {order.notes && (
              <div className="md:col-span-2">
                <p className="text-sm text-neutral-600 mb-1">Customer Notes</p>
                <p className="text-neutral-700">{order.notes}</p>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.pdf"
        className="hidden"
        onChange={handleUploadPaymentProof}
      />
    </div>
  );
};

export default EnquiryDetails;